import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PlanPageActions } from '@lyceo/shared/plan/data-access';
import { PupilContextActions } from '@lyceo/shared/pupil-context/data-access';
import { catchError, exhaustMap, filter, map, of } from 'rxjs';
import { PupilFacade } from './pupil.facade';
import { isDefined } from '@lyceo/shared/util';
import { routerNavigationAction } from '@ngrx/router-store';
import { PupilAPIActions } from './pupil.actions';
import { PupilService } from '../../pupil.service';
import { HomiSettingsActions } from '@lyceo/shared/homi-settings/data-access';

@Injectable()
export class PupilEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly pupilFacade: PupilFacade = inject(PupilFacade);
  private readonly pupilService: PupilService = inject(PupilService);

  public setPupilContext$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanPageActions.init),
      map(() => this.pupilFacade.paramPupilId()),
      filter(isDefined),
      map((id) =>
        PupilContextActions.setPupilContext({
          pupilContext: {
            homiPersonId: id,
          },
        }),
      ),
    );
  });

  public pupilLoaded$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigationAction),
      filter((action) => action.payload.event.url.startsWith('/pupils/')),
      filter(() => !this.pupilFacade.paramPupilLoaded()),
      map(() => PupilAPIActions.getPupilProfileById()),
    );
  });

  public getHomiSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PupilAPIActions.getPupilProfileById),
      map(() => this.pupilFacade.paramPupilId()),
      filter(isDefined),
      map((id) =>
        HomiSettingsActions.getSettingsByPersonId({
          homiPersonId: id,
        }),
      ),
    );
  });

  public getPupil$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PupilAPIActions.getPupilProfileById),
      map(() => this.pupilFacade.paramPupilId()),
      filter(isDefined),
      exhaustMap((paragraphId) =>
        this.pupilService.getPersonProfile(paragraphId).pipe(
          map((pupilProfile) =>
            PupilAPIActions.getPupilProfileByIdSuccess({
              pupilProfile: pupilProfile,
            }),
          ),
          catchError((error: Error) =>
            of(PupilAPIActions.getPupilProfileByIdFailure({ error })),
          ),
        ),
      ),
    );
  });
}
